import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo, useState } from "react";

import { UsePaginationFetchMoreProps } from "components/Table/Pagination/types";
import usePaginationCursorReset from "components/Table/Pagination/usePaginationCursorReset";
import useSearchStackTasks from "shared/Stack/useSearchTasks";
import useStackTasksFiltersVariables from "shared/Stack/useSearchTasksSuggestions/useFiltersVariables";
import useSearchStackTasksSuggestions from "shared/Stack/useSearchTasksSuggestions";
import { SearchQueryRequestedPage } from "types/generated";

import { STACK_TASKS_LIMIT, STACK_TASKS_LIMIT_STORAGE_KEY } from "./constants";

const useStackTasks = (stackId: string) => {
  const [storedRowsPerPage] = useLocalStorage<number>(STACK_TASKS_LIMIT_STORAGE_KEY);
  const [storedCursor, setStoredCursor] = useState<string | null>(null);
  const [storedRequestedPage, setStoredRequestedPage] = useState(SearchQueryRequestedPage.First);

  const { fullTextSearch, sortOptionFields } = useStackTasksFiltersVariables();

  const onFetchItems = useCallback(({ cursor, requestedPage }: UsePaginationFetchMoreProps) => {
    setStoredCursor(cursor);
    setStoredRequestedPage(requestedPage);
  }, []);

  const resetPagination = useCallback(() => {
    setStoredCursor(null);
    setStoredRequestedPage(SearchQueryRequestedPage.First);
  }, []);

  const paginationResetKey = useMemo(
    () => ({ stackId, fullTextSearch, sortOptionFields }),
    [stackId, fullTextSearch, sortOptionFields]
  );

  const { cursor, requestedPage } = usePaginationCursorReset({
    resetKey: paginationResetKey,
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  });

  const { tasks, endCursor, startCursor, error, initialLoading, loadingContent } =
    useSearchStackTasks(stackId, {
      first: storedRowsPerPage || STACK_TASKS_LIMIT,
      after: cursor,
      fullTextSearch,
      orderBy: sortOptionFields,
      requestedPage,
    });

  const { totalCount } = useSearchStackTasksSuggestions(stackId, {
    fields: [],
    fullTextSearch,
  });

  const hasRows = tasks.length > 0;
  const hasSearchQuery = !!fullTextSearch;
  const isEmpty = !hasRows && !loadingContent && !hasSearchQuery;
  const searchIsEmpty = !hasRows && !loadingContent && hasSearchQuery;

  return {
    tasks,
    error,
    isPageLoading: initialLoading,
    loadingContent,
    isEmpty,
    searchIsEmpty,
    onFetchItems,
    endCursor,
    startCursor,
    totalCount,
    paginationResetKey,
  };
};

export default useStackTasks;
